// File: src/components/EventPreview/EventTags.jsx
import React from 'react';
import {
  LuDice5, LuShirt, LuBuilding2, LuPartyPopper, LuUsers, LuLock, LuHeartHandshake, LuBaby
} from 'react-icons/lu';

function ageLabelFromEvent(e) {
  if (!e?.ageGateOn) return '';
  const min = Math.max(18, Number(e.ageMin ?? 18));
  const max = e.ageMax == null ? null : Number(e.ageMax);
  if (max == null) return `${min}+`;
  if (min === max) return `${min} only`;
  return `${min}–${max}`;
}

export default function EventTags({ event }) {
  const {
    mode,
    purposeTag,
    eventDate,
    startTime12,
  venueType = '',
  clothing = '',

    // ✅ Neutral audience fields
    neutralGenderGate = 'all',
    neutralOrientation = 'open',

    // Mixer fields (legacy)
    genderGate = 'all',
    orientationType = '',

    // Classic fields (matchmaking)
    classicGenderGate = 'all',
    classicOrientation = '',

    // Soft-gate
    affinity = false,
  } = event || {};


  const venueLabel = String(venueType || '').trim();
const dressLabel = String(clothing || '').trim();

const venueLower = venueLabel.toLowerCase();
const dressLower = dressLabel.toLowerCase();

// ONLY treat blanks / placeholder text as "unset"
const isUnsetVenue =
  !venueLabel ||
  venueLower === 'select…' ||
  venueLower === 'select...' ||
  venueLower === 'select venue' ||
  venueLower === 'venue' ||
  venueLower === 'venue type';

const isUnsetDress =
  !dressLabel ||
  dressLower === 'select…' ||
  dressLower === 'select...' ||
  dressLower === 'select dress code' ||
  dressLower === 'dress code' ||
  dressLower === 'attire';

  const isUpcoming = checkIfUpcoming(eventDate, startTime12);
  const modeN = String(mode ?? '').toLowerCase().trim();
  const purposeN = String(purposeTag ?? '').toLowerCase().trim();

  const modeTagLabel = (() => {
    // NEW: neutral → Host-Led
    if (modeN === 'neutral') return 'Host-Led';

    const isReveal =
      modeN.includes('reveal') ||
      modeN.includes('the match') ||
      (/\bmatch\b/.test(modeN) && !modeN.includes('matchmaking'));
    if (modeN.includes('mixer')) return 'Color-Mixer';
    if (isReveal) return 'The Match';
    return 'Speed-Match'; // default legacy
  })();

  // ── Gender gate tag (Classic Friendship/Networking OR Mixer Friendship/Networking)
  const genderGateLabel = (() => {
    // ✅ Neutral: show whenever gate is set (regardless of purpose)
    if (modeN === 'neutral') {
      const g = String(neutralGenderGate || 'all').trim().toLowerCase();
      if (g === 'men_only' || g === 'menonly') return 'Men Only';
      if (g === 'women_only' || g === 'womenonly') return 'Women Only';
      return '';
    }

    // Existing behavior: only show for Friendship/Networking
    if (purposeN !== 'friendship' && purposeN !== 'networking') return '';

    const rawGate = modeN === 'matchmaking' ? classicGenderGate : genderGate;

    const g = String(rawGate || '').trim().toLowerCase();
    if (g === 'men_only' || g === 'menonly') return 'Men Only';
    if (g === 'women_only' || g === 'womenonly') return 'Women Only';
    return '';
  })();

  // ── Orientation tag (Dating)
  const orientationLabel = (() => {
    if (purposeN !== 'dating') return '';

    let rawOrientation = '';
    if (modeN === 'reveal') {
      rawOrientation = event?.revealMode?.orientation || '';
    } else if (modeN === 'matchmaking') {
      rawOrientation = classicOrientation || '';
    } else {
      // mixer dating
      rawOrientation = orientationType || '';
    }

    const o = canonicalizeOrientation(rawOrientation);
    if (o === 'gay_men')       return 'Gay Men';
    if (o === 'gay_women')     return 'Gay Women';
    if (o === 'heterosexual')  return 'Heterosexual';
    if (o === 'open')          return 'Open to All Genders'; // open + bi collapsed
    return '';
  })();

    // ✅ Neutral orientation tag (independent of purpose)
  const neutralOrientationTag = (() => {
    if (modeN !== 'neutral') return '';

    // allow tag-driven LGBTQ signal too
    const tagsArr = Array.isArray(event?.tags) ? event.tags.map(t => String(t).toLowerCase()) : [];
    if (tagsArr.includes('lgbtq')) return 'LGBTQ+';

    const o = String(neutralOrientation || 'open').trim().toLowerCase();
    if (o === 'lgbtq' || o === 'lgbtq+') return 'LGBTQ+';
    if (o === 'gay_men') return 'Gay Men';
    if (o === 'gay_women') return 'Gay Women';
    if (o === 'straight' || o === 'heterosexual') return 'Heterosexual';
    return ''; // open => no tag
  })();


  // ── Age tag (when configured)
  const ageTagLabel = ageLabelFromEvent(event);

  const tags = [
    { icon: LuDice5, label: modeTagLabel },

    // Purpose (avoid duplicating the mode label)
    ...(purposeTag &&
      purposeTag.trim().length &&
      purposeTag.trim().toLowerCase() !== modeTagLabel.toLowerCase()
        ? [{ icon: LuPartyPopper, label: purposeTag.trim() }]
        : []),

    // Gender gate (Classic/Mixer Friendship/Networking)
    ...(genderGateLabel ? [{ icon: LuUsers, label: genderGateLabel }] : []),

    // Age requirement
    ...(ageTagLabel ? [{ icon: LuBaby, label: ageTagLabel }] : []),

    // Affinity (restricted identity group)
    ...(affinity ? [{ icon: LuLock, label: 'Restricted Affinity' }] : []),

   // ✅ Sexual orientation (Host-Led mode)
    ...(neutralOrientationTag ? [{ icon: LuHeartHandshake, label: neutralOrientationTag }] : []),

    // Orientation (Dating: Classic/Mixer/Reveal)
    ...(orientationLabel ? [{ icon: LuHeartHandshake, label: orientationLabel }] : []),

    // Venue & dress (only if set)
...(!isUnsetDress ? [{ icon: LuShirt, label: dressLabel }] : []),
...(!isUnsetVenue ? [{ icon: LuBuilding2, label: venueLabel }] : []),

  ];

  return (
    <div style={S.row}>
      {tags.map(({ icon: Icon, label }, idx) => (
        <div key={`${label}-${idx}`} style={S.tag}>
          <Icon style={S.icon} />
          <span style={S.txt}>{label}</span>
        </div>
      ))}
    </div>
  );
}

/* utils */
function checkIfUpcoming(eventDate, startTime12) {
  if (!eventDate || !startTime12) return false;
  const [yyyy, mm, dd] = eventDate.split('-').map(Number);
  const start = new Date(
    `${yyyy}-${String(mm || 1).padStart(2, '0')}-${String(dd || 1).padStart(2, '0')} ${startTime12}`
  );
  return start > new Date();
}

function canonicalizeOrientation(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'gay_men' || s === 'gay men' || s === 'mlm' || s === 'men loving men') return 'gay_men';
  if (s === 'gay_women' || s === 'gay women' || s === 'wlw' || s === 'women loving women' || s === 'lesbian')
    return 'gay_women';
  if (s === 'straight' || s === 'hetero' || s === 'heterosexual') return 'heterosexual';

  // collapse “open” + “bisexual” together
  if (
    s === 'open' ||
    s === 'bisexual' ||
    s === 'bi' ||
    s === 'all' ||
    s === 'all genders' ||
    s === 'all_genders' ||
    s === 'gender fluid' ||
    s === 'sexually fluid'
  ) return 'open';

  // also catch flattened variants
  const flat = s.replace(/[^a-z]/g, '');
  if (['gaymen','menlovingmen','mlm'].includes(flat)) return 'gay_men';
  if (['gaywomen','womenlovingwomen','wlw','lesbian'].includes(flat)) return 'gay_women';
  if (['straight','hetero','heterosexual','menandwomen'].includes(flat)) return 'heterosexual';
  if (['open','bisexual','bi','all','allgenders','genderfluid','sexuallyfluid'].includes(flat)) return 'open';

  return '';
}

/* styles */
const S = {
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    margin: '0 20px 16px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    marginTop: 20,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 16px',
  },
  icon: { fontSize: 12, color: '#374151' },
  txt: { fontSize: 12, fontWeight: 700, color: '#0b0b0b' },
};
